import { Domingo } from '@fiverr-private/domingo';

type AllowedLocale = Parameters<ReturnType<typeof Domingo>['locale']>[0];
import { getShortLanguageCode } from '../shortLanguageCode';
import {
    SUPPORTED_FIVERR_LOCALES_MAP,
    DEFAULT_FIVERR_LOCALE as DEFAULT_FIVERR_LOCALE_CONST,
    LOCALE_TO_SUBDOMAIN_MAP,
    SUPPORTED_FIVERR_LANGUAGES as SUPPORTED_FIVERR_LANGUAGES_CONST,
} from './constants';

export const DEFAULT_FIVERR_LOCALE = DEFAULT_FIVERR_LOCALE_CONST;
export const SUPPORTED_FIVERR_LOCALES = Object.keys(SUPPORTED_FIVERR_LOCALES_MAP);
export const DEFAULT_FIVERR_LANGUAGE = getShortLanguageCode(DEFAULT_FIVERR_LOCALE);
export const SUPPORTED_FIVERR_LANGUAGES = SUPPORTED_FIVERR_LANGUAGES_CONST;
export { LOCALE_TO_SUBDOMAIN_MAP };

/**
 * Build a map of the supported Fiverr locales in the form of:
 * {
 *     EN_US: 'en-US',
 *     DE_DE: 'de-DE',
 *     ES: 'es,
 *     ...
 * }
 * So consumers can check for specific locale in their code.
 * @returns {{localeKey: String}} - the native name of the locale
 */
const localesKeys = {};
Object.keys(SUPPORTED_FIVERR_LOCALES_MAP).forEach((locale) => {
    const localeKey = locale.toUpperCase().replace(/-/g, '_');
    localesKeys[localeKey] = locale;
});
export const SUPPORTED_FIVERR_LOCALES_KEYS = localesKeys;

/**
 * Check if Fiverr supports this locale.
 * Meaning: we have a Fiverr experience in this locale (UI language).
 * @param locale {string} - check for existence as-is, no trimming, etc.
 * @returns {boolean} - the locale is supported or not
 */
export const isSupportedFiverrLocale = (locale) => !!SUPPORTED_FIVERR_LOCALES_MAP[locale];

/**
 * Returns the native name of the locale.
 * @param locale {string} - check for existence as-is, no trimming, etc.
 * @returns {string} - the native name of the locale
 */
export const getFiverrLocaleNativeName = (locale) => SUPPORTED_FIVERR_LOCALES_MAP[locale] || locale;

const MAIN_SUBDOMAIN = LOCALE_TO_SUBDOMAIN_MAP[DEFAULT_FIVERR_LOCALE];
const PROTOCOL = 'https://';
// Bare `fiverr.com` / `dev.fiverr.com` — needs www-normalization after removeLocale
// (subsystem hosts like pro.*, okteto.*, business.* don't match and stay bare-of-www).
const BARE_FIVERR_HOST_REGEX = /^(dev\.)?fiverr\.com$/;

/**
 * Replace (or remove) the locale subdomain on a Fiverr hostname.
 * Delegates to @fiverr-private/domingo so all Fiverr URL shapes
 * (standard, pro, okteto, lp, auth, verification) are handled uniformly.
 *
 * @param {string} hostname bare Fiverr hostname (no protocol)
 * @param {string} targetSubdomain target locale subdomain; pass MAIN_SUBDOMAIN ('www') to remove
 * @returns {string} the rewritten hostname
 */
export const replaceLocaleSubdomain = (hostname: string, targetSubdomain: string): string => {
    const url = `${PROTOCOL}${hostname}`;
    const route = Domingo(url);
    const transformed =
        targetSubdomain === MAIN_SUBDOMAIN
            ? route.removeLocale().url
            : route.locale(targetSubdomain as AllowedLocale).url;
    const result = transformed.slice(PROTOCOL.length);

    // Canonical Fiverr English host is `www.fiverr.com`; Domingo strips locale to bare form.
    if (targetSubdomain === MAIN_SUBDOMAIN && BARE_FIVERR_HOST_REGEX.test(result)) {
        return `${MAIN_SUBDOMAIN}.${result}`;
    }
    return result;
};

/**
 * Convert locale to its matching subdomain (e.g "en-US" -> "www", "pt-BR" -> "br").
 * Fallback is the main subdomain which is "www".
 * @param {string} locale
 * @returns the matching subdomain
 */
export const getLocaleSubdomain = (locale) => LOCALE_TO_SUBDOMAIN_MAP[locale] || MAIN_SUBDOMAIN;
